import { getBaseEmailTemplate } from './base-template';
import { plainTextToEmailHtml } from './email-plain-text-html';

interface RoomShareData {
  roomName: string;
  accountName?: string;
  customMessage?: string;
  shareableUrl: string; // Required for room shares
  inviterName?: string;
  recipientName?: string;
  /** When true, omit the "Want to create your own Smart Rooms?" promo block (tenant feature flag). */
  hidePromotionalSection?: boolean;
}

export function getRoomShareTemplate(data: RoomShareData): string {
  const inviterGreeting = data.inviterName
    ? `
      <tr>
        <td style="padding: 0 0 12px 0;">
          <p style="font-size: 16px; color: #111827; margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.45;">
            <strong style="font-weight: 700;">${data.inviterName}</strong> has invited you to collaborate.
          </p>
        </td>
      </tr>
    `
    : '';

  /** Single highlight card: room name + Access Room (one container). */
  const roomCardWithCta = `
    <tr>
      <td style="padding: 12px 0 0 0;">
        <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="background-color: #f8f9ff; border-radius: 12px; border: 1px solid #e0e7ff;">
          <tr>
            <td style="padding: 14px 16px 12px; text-align: center;">
              <p style="margin: 0; padding: 0; font-size: 11px; color: #6b7280; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.4;">
                Room
              </p>
              <p style="margin: 6px 0 0 0; padding: 0; font-size: 20px; color: #111827; font-weight: 700; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.25;">
                ${data.roomName}
              </p>
            </td>
          </tr>
          <tr>
            <td style="border-top: 1px solid #e0e7ff; padding: 14px 16px 16px; text-align: center;">
              <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="margin: 0 auto;">
                <tr>
                  <td align="center" style="border-radius: 10px; background-color: #1e3a8a;">
                    <!--[if mso]>
                    <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="${data.shareableUrl}" style="height:44px;v-text-anchor:middle;width:200px;" arcsize="23%" strokecolor="#1e3a8a" fillcolor="#1e3a8a">
                      <w:anchorlock/>
                      <center style="color:#ffffff;font-family:sans-serif;font-size:15px;font-weight:600;">Access Room</center>
                    </v:roundrect>
                    <![endif]-->
                    <!--[if !mso]><!-->
                    <a href="${data.shareableUrl}" target="_blank" style="display: inline-block; padding: 12px 32px; background-color: #1e3a8a; color: #ffffff; text-decoration: none; border-radius: 10px; font-weight: 600; font-size: 15px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; letter-spacing: 0.3px; line-height: 1.4; mso-hide: all;">
                      Access Room
                    </a>
                    <!--<![endif]-->
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  `;

  // Custom message box (newlines → <br />; HTML-escaped)
  const customMessageHtml = data.customMessage ? plainTextToEmailHtml(data.customMessage) : '';
  const customMessageBox = customMessageHtml
    ? `
    <tr>
      <td style="padding: 16px 0 0 0;">
        <p style="margin: 0 0 6px 0; padding: 0; font-size: 12px; font-weight: 600; color: #6b7280; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">Note from the sender</p>
        <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="background-color: #f0f4ff; border-radius: 10px; border-left: 3px solid #1e3a8a;">
          <tr>
            <td style="padding: 12px 14px;">
              <p style="margin: 0; padding: 0; color: #1e3a8a; font-size: 14px; line-height: 1.5; font-style: italic; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
                &quot;${customMessageHtml}&quot;
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  `
    : '';

  // Recipient greeting
  const recipientGreeting = data.recipientName
    ? `
      <tr>
        <td style="padding: 0 0 10px 0;">
          <p style="font-size: 17px; color: #111827; margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.45;">
            Hi <strong style="font-weight: 700;">${data.recipientName}</strong>,
          </p>
        </td>
      </tr>
    `
    : `
      <tr>
        <td style="padding: 0 0 10px 0;">
          <p style="font-size: 17px; color: #111827; margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.45;">
            Hello,
          </p>
        </td>
      </tr>
    `;

  // Get base URL for logo
  const baseUrl = process.env.NEXTAUTH_URL || process.env.NEXT_PUBLIC_APP_URL || 'https://www.smartroomsxp.com';
  const logoUrl = `${baseUrl}/sr_logo.png`;

  const content = `
    <div class="header">
      <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="margin-bottom: 12px;">
        <tr>
          <td align="center" style="padding: 0 0 10px 0;">
            <a href="https://www.smartroomsxp.com" target="_blank" style="text-decoration: none;">
              <img src="${logoUrl}" alt="Smart Rooms XP" width="160" height="40" style="display: block; margin: 0 auto; height: auto; max-width: 160px;" />
            </a>
          </td>
        </tr>
      </table>
      <h1 style="margin: 0; padding: 0; font-size: 26px; font-weight: 700; letter-spacing: -0.5px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.15;">You're Invited!</h1>
    </div>
    <div class="content">
      <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation">
        ${recipientGreeting}
        ${inviterGreeting}
        ${roomCardWithCta}
        ${customMessageBox}
        <tr>
          <td align="center" style="padding: 14px 0 0 0;">
            <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="background-color: #f9fafb; border-radius: 10px; border: 1px solid #e5e7eb;">
              <tr>
                <td style="padding: 12px 14px;">
                  <p style="margin: 0; padding: 0; color: #6b7280; font-size: 13px; line-height: 1.5; text-align: center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
                    Use the <strong style="color: #4b5563; font-weight: 600;">Access Room</strong> button to open this space. If the button doesn&apos;t work, copy the link from the plain-text version of this email.
                  </p>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        
        ${data.hidePromotionalSection ? '' : `<!-- Signup CTA Section -->
        <tr>
          <td style="padding: 20px 0 0 0;">
            <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="background-color: #f0f4ff; border-radius: 12px; border: 1px solid #e0e7ff;">
              <tr>
                <td style="padding: 24px; text-align: center;">
                  <p style="margin: 0 0 12px 0; padding: 0; color: #1e3a8a; font-size: 15px; font-weight: 600; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
                    Want to create your own Smart Rooms?
                  </p>
                  <p style="margin: 0 0 16px 0; padding: 0; color: #4b5563; font-size: 14px; line-height: 1.5; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
                    Get started with Smart Rooms XP and build your own collaboration spaces.
                  </p>
                  <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="margin: 0 auto;">
                    <tr>
                      <td align="center" style="border-radius: 8px; background-color: #ffffff; border: 2px solid #1e3a8a;">
                        <a href="https://www.smartroomsxp.com?utm_source=email&utm_medium=room_invite&utm_campaign=signup" target="_blank" style="display: inline-block; padding: 12px 24px; color: #1e3a8a; text-decoration: none; font-weight: 600; font-size: 14px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
                          Get Started →
                        </a>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        `}
        <tr>
          <td style="padding: 20px 0 0 0;">
            <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="border-top: 1px solid #e5e7eb;">
              <tr>
                <td style="padding: 16px 0 0 0;">
                  <p style="margin: 0; padding: 0; color: #6b7280; font-size: 12px; line-height: 1.6; text-align: left; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
                    By clicking the access link, you acknowledge that you are engaging with the Smart Rooms XP workspace created by <strong style="font-weight: 600; color: #374151;">${data.inviterName || 'the sender'}</strong>. Your access is governed by our <a href="https://www.smartroomsxp.com/terms" target="_blank" style="color: #1e3a8a; text-decoration: underline;">Terms of Use</a> and <a href="https://www.smartroomsxp.com/privacy" target="_blank" style="color: #1e3a8a; text-decoration: underline;">Privacy Policy</a>. This email was sent as part of a legitimate business interaction initiated by <strong style="font-weight: 600; color: #374151;">${data.inviterName || 'the sender'}</strong>. If you believe this invitation was not intended for you, please disregard this email.
                  </p>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </div>
  `;

  return getBaseEmailTemplate(
    content,
    'You are receiving this email because you have been invited to collaborate.',
    undefined,
    undefined,
    undefined,
    "You've been invited to collaborate on Smart Rooms"
  );
}

export function getRoomShareText(data: RoomShareData): string {
  const inviterInfo = data.inviterName ? `\n${data.inviterName} has invited you to collaborate!\n` : '';
  const greeting = data.recipientName ? `Hi ${data.recipientName},` : 'Hello,';
  const senderName = data.inviterName || 'the sender';

  return `
Room Invitation

${greeting}
${inviterInfo}
${data.customMessage ? `"${data.customMessage}"\n\n` : ''}You've been invited to access ${data.roomName}.

Access Room: ${data.shareableUrl}

Click the link above to access the room and start collaborating.
${data.hidePromotionalSection ? '' : `
Want to create your own Smart Rooms?
Get started: https://www.smartroomsxp.com?utm_source=email&utm_medium=room_invite&utm_campaign=signup
`}
This message was sent from Smart Rooms XP.

---

By clicking the access link, you acknowledge that you are engaging with the Smart Rooms XP workspace created by ${senderName}. Your access is governed by our Terms of Use (https://www.smartroomsxp.com/terms) and Privacy Policy (https://www.smartroomsxp.com/privacy). This email was sent as part of a legitimate business interaction initiated by ${senderName}. If you believe this invitation was not intended for you, please disregard this email.
  `.trim();
}

